import React from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { 
  CheckCircle2, 
  Clock, 
  MessageSquare, 
  Eye, 
  ArrowLeft,
  Rocket
} from 'lucide-react';

function ReadyStep({ 
  testType,
  questionsCount,
  onBack, 
  onStartInterview,
  isLoading, 
  isReady
}) {

  const totalQuestions = questionsCount || 15;
  // Determine test duration based on type
  const getEstimatedDuration = () => {
    switch (testType?.toLowerCase()) {
      case 'c':
      case 'qc':
        return '30-45 minutes';
      case 'p':
      case 'b':
        return '15-20 minutes';
      case 'apt':
        return '30 minutes';
      default:
        return '30-60 minutes';
    }
  };

  const getTestTypeName = () => {
    const typeMap = {
      c: 'Coding Test',
      t: 'Screening Interview',
      p: 'Personality Assessment',
      b: 'Behavioral Interview',
      o: 'Communication Test',
      a: 'Personality & Behavioral',
      rs: 'Realtime Screening',
      qc: 'Quick Coding',
      qt: 'Quick Screening',
      apt: 'Aptitude Test',
    };
    return typeMap[testType?.toLowerCase()] || 'Interview';
  };

  return (
    <Card className="max-w-4xl mx-auto">
      <CardContent className="p-4 sm:p-6">
        {/* Success header */}
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 rounded-full bg-green-500/10 flex items-center justify-center shrink-0">
            <CheckCircle2 className="w-5 h-5 text-green-500" />
          </div>
          <div>
            <h2 className="text-lg font-semibold">You're All Set!</h2>
            <p className="text-sm text-muted-foreground">Your system is ready. Here's what to expect:</p>
          </div>
        </div>

        {/* Info bar - horizontal compact layout */}
        <div className="flex flex-wrap items-center gap-x-6 gap-y-2 p-3 rounded-lg bg-muted/50 mb-4">
          <div className="flex items-center gap-2">
            <MessageSquare className="w-4 h-4 text-primary" />
            <span className="text-sm"><strong>{totalQuestions || '~20'}</strong> questions</span>
          </div>
          <div className="flex items-center gap-2">
            <Clock className="w-4 h-4 text-primary" />
            <span className="text-sm"><strong>{getEstimatedDuration()}</strong> estimated</span>
          </div>
          <div className="flex items-center gap-2">
            <Eye className="w-4 h-4 text-primary" />
            <span className="text-sm">Stay <strong>visible</strong> on camera</span>
          </div>
        </div>

        {/* Quick reminders */}
        <div className="p-4 rounded-lg border bg-card mb-4">
          <h3 className="font-semibold text-base mb-3">Quick Reminders</h3>
          <ul className="text-sm text-muted-foreground space-y-2">
            <li className="flex items-start gap-2">
              <span className="text-primary mt-0.5">•</span>
              <span>Answer within a few seconds - delays may skip to the next question</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-primary mt-0.5">•</span>
              <span>Keep answers clear and concise</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-primary mt-0.5">•</span>
              <span>The test will auto-complete if you pause too long</span>
            </li>
          </ul>
        </div>

        {/* Action buttons */}
        <div className="flex items-center justify-between gap-3">
          <Button 
            variant="outline" 
            onClick={onBack}
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back
          </Button>
          <Button 
            disabled={!isReady || isLoading}
            onClick={onStartInterview}
          >
            {(isLoading || !isReady) ? (
              <span className="animate-pulse">Starting...</span>
            ) : (
              <>
                <Rocket className="w-4 h-4 mr-2" />
                Start {getTestTypeName()}
              </>
            )}
          </Button>
          {!isReady && (
            <p className="text-sm text-muted-foreground mt-2 text-center animate-pulse">
              Setting up environment for the interview…
            </p>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

export default ReadyStep;
